<script setup>
import { onMounted, ref, watch } from "vue";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";
import ArgonTable from "@/components/table/ArgonTable.vue";
import ArgonTd from "@/components/table/ArgonTd.vue";
import ActionsDropdown from "@/components/dropdown/ActionsDropdown.vue";
import ModalZone from "./ModalZone.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import ConfirmModal from "@/components/modal/ConfirmModal.vue";
import { useConfirmModal } from "@/composables/useConfirmModal";
import { zoneStore } from "@/_store/zoneStore";
import { argonTableStore } from "@/_store/argonTableStore";
import { userStore } from "@/_store/userStore";
import { errorUtils, fileUtils } from "@/utils";

const store = zoneStore();
const tableStore = argonTableStore();
const userStores = userStore();

const showModal = ref(false);
const selectedZoneId = ref(null);
const snackbar = ref(null);
const {
  showConfirmModal,
  confirmTitle,
  confirmText,
  confirmButtonText,
  cancelButtonText,
  confirmButtonClass,
  cancelButtonClass,
  confirmLoading,
  openConfirmModal,
  handleConfirmModal,
} = useConfirmModal();

const fieldFilters = ref([
  {
    field: "name",
    description: "Nombre",
  },
  {
    field: "postal_codes",
    description: "Codigo postal",
  },
]);

const columns = ref([
  { header: "Nombre", field: "name", sort: false, position: "start" },
  {
    header: "Abreviatura",
    field: "abbreviation",
    sort: false,
    position: "start",
  },
  {
    header: "Codigos postales",
    field: "postal_codes",
    sort: false,
    position: "start",
  },
  {
    header: "",
    field: "actions",
    sort: false,
    position: "center",
    width: "w-1",
  },
]);

onMounted(() => {
  tableStore.clear();
  const savedFilters = userStores.filtersPage.zones?.filters || [];
  tableStore.changeFilters(savedFilters);
  reloadPage();
});

function reloadPage() {
  userStores.filtersPage.zones = {
    filters: tableStore.filters,
    filtersFavorite: tableStore.filtersFavorite,
  };

  store.getZones(tableStore.pagination, tableStore.query, tableStore.sort);
}

async function exportZones() {
  const bytes = await store.exportar(
    tableStore.pagination,
    tableStore.query,
    tableStore.sort
  );

  fileUtils.downloadBytesFile(
    bytes,
    fileUtils.generateDownloadFileName("xlsx")
  );
}

function onSortChanged() {
  tableStore.clearPagination();
  reloadPage();
}

function openNewZoneModal() {
  selectedZoneId.value = null;
  showModal.value = true;
}

function editZone(zone) {
  selectedZoneId.value = zone?._id || null;
  showModal.value = true;
}

function closeModal() {
  showModal.value = false;
}

function successSaveZone() {
  showModal.value = false;
  reloadPage();
}

function openDeleteZonesConfirm(zoneIds = tableStore.checkedList) {
  const normalizedZoneIds = Array.isArray(zoneIds) ? zoneIds.filter(Boolean) : [];
  if (!normalizedZoneIds.length) {
    snackbar.value = {
      title: "Error",
      message: "Debe seleccionar al menos una zona.",
      type: "danger",
      color: "white",
    };
    return;
  }

  const isSingleZone = normalizedZoneIds.length === 1;
  openConfirmModal({
    title: isSingleZone
      ? "Estas seguro de querer eliminar esta zona?"
      : "Estas seguro de querer eliminar las zonas seleccionadas?",
    text: "No podra revertir esta accion.",
    confirmButtonText: "Eliminar",
    confirmButtonClass: "btn btn-xs ms-1 bg-gradient-danger",
    onConfirm: () => {
      store.deleteZone(normalizedZoneIds);
    },
  });
}

function getPosition(field) {
  const column = columns.value.find((col) => col.field === field);
  return column?.position || "start";
}

function postalCodesLabel(value) {
  if (!Array.isArray(value) || !value.length) return "-";
  return value.join(", ");
}

function getRowActionsMenuId(zoneItem) {
  return `zone-row-actions-${String(zoneItem?._id || "unknown").replace(
    /[^a-zA-Z0-9_-]/g,
    "_"
  )}`;
}

function getRowActions() {
  return [
    [{ label: "Editar", event: "edit" }],
    [{ label: "Eliminar", event: "delete", type: "danger" }],
  ];
}

function onRowAction(zoneItem, action) {
  const actionEvent = action?.event;
  if (!actionEvent || !zoneItem?._id) return;

  if (actionEvent === "edit") {
    editZone(zoneItem);
    return;
  }

  if (actionEvent === "delete") {
    openDeleteZonesConfirm([zoneItem._id]);
  }
}

const closeSnackbar = () => {
  snackbar.value = null;
};

watch(
  () => store.zoneDeleted,
  (value) => {
    if (value) {
      tableStore.clearSelection();
      reloadPage();
      snackbar.value = {
        title: "OK",
        message: "Zona eliminada",
        type: "info",
        color: "white",
      };
    }
  }
);

watch(
  () => store.error,
  (value) => {
    if (!value) return;
    snackbar.value = {
      title: "Error",
      message: errorUtils.getErrorMessageHtml(value),
      type: "danger",
      color: "white",
    };
  }
);
</script>

<template>
  <div class="card zones-table-view">
    <div class="card-body zones-table-body">
      <argon-table
        v-if="store.zones"
        :columns="columns"
        :items="store.zones"
        :field-filters="fieldFilters"
        :show-config-option="true"
        :show-filters="false"
        :show-importar="false"
        @filter-changed="reloadPage"
        @on-reload="reloadPage"
        @sort-changed="onSortChanged"
        @page-changed="reloadPage"
        @new-item="openNewZoneModal"
        @on-click="editZone"
        @export-data="exportZones"
      >
        <template #buttons>
          <div
            class="dropdown d-inline"
            data-dropdown-static
            style="--dropdown-gap: 4px"
          >
            <argon-button
              id="navbarDropdownPrinter"
              color="primary"
              variant="outline"
              class="dropdown-toggle me-2 btn-xs"
              data-bs-toggle="dropdown"
              data-bs-display="static"
              aria-expanded="false"
            >
              <i class="fa fa-exchange me-sm-2"></i>Acciones
            </argon-button>
            <ul
              class="dropdown-menu z-index-1030 dropdown-menu-lg-start px-2 py-3"
              aria-labelledby="navbarDropdownPrinter"
            >
              <li>
                <a
                  class="dropdown-item border-radius-md text-danger"
                  @click="() => openDeleteZonesConfirm()"
                >
                  Eliminar
                </a>
              </li>
            </ul>
          </div>
        </template>
        <template #default="{ item }">
          <argon-td :position="getPosition('name')" :text-primary="item.name" />
          <argon-td
            :position="getPosition('abbreviation')"
            :text-primary="item.abbreviation"
          />
          <argon-td
            :position="getPosition('postal_codes')"
            :text-primary="postalCodesLabel(item.postal_codes)"
            max-width="380px"
          />
          <argon-td :position="getPosition('actions')">
            <div class="table-row-actions">
              <actions-dropdown
                :menu-id="getRowActionsMenuId(item)"
                :actions="getRowActions()"
                aria-label="Acciones de la zona"
                trigger-class="table-action-button"
                menu-class="table-action-menu-dropdown"
                @action="(action) => onRowAction(item, action)"
              />
            </div>
          </argon-td>
        </template>
      </argon-table>
    </div>

    <modal-zone
      v-if="showModal"
      :zone-id="selectedZoneId"
      @close="closeModal"
      @success="successSaveZone"
    />
    <confirm-modal
      v-if="showConfirmModal"
      v-model="showConfirmModal"
      :title="confirmTitle"
      :text="confirmText"
      :confirm-button-text="confirmButtonText"
      :cancel-button-text="cancelButtonText"
      :confirm-button-class="confirmButtonClass"
      :cancel-button-class="cancelButtonClass"
      :loading="confirmLoading"
      @confirm="handleConfirmModal"
    />

    <div class="position-fixed top-0 end-0 z-index-2 p-3">
      <argon-snackbar
        v-if="snackbar"
        :title="snackbar.title"
        :description="snackbar.message"
        :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
        :color="snackbar.color"
        :close-handler="closeSnackbar"
      />
    </div>
  </div>
</template>

<style scoped>

:deep(thead th.text-center.w-1:last-child),
:deep(tbody td.text-center:last-child) {
  max-width: 48px;
  min-width: 48px;
  width: 48px;
}
</style>
